import { ButtonLink } from '@landler/component-library';
import { ErrorBoundary } from 'react-error-boundary';

import { usePlotGroupId } from '@/components/PlotGroups/PlotGroupsContextProvider';
import { SuspenseBoundary } from '@/components/SuspenseBoundary';
import { Icon } from '@/components/widgets/a/Icon/Icon';
import { useOrganizationWorkstream } from '@/hooks/useOrganizationWorkstream';
import { useOrganizationId } from '@/pages/shared/hooks/useOrganizationId';
import { useOrganizationPlotGroups } from '@/pages/shared/hooks/useOrganizationPlotGroup';
import { usePlot } from '@/pages/shared/hooks/usePlot';

import { BREADCRUMB_TYPE, BreadcrumbItem } from './constants';

export const BreadcrumbSegment = ({
  label,
  to,
  icon,
  type,
}: Pick<BreadcrumbItem, 'label' | 'to' | 'icon' | 'type'>) => {
  const BaseComponent = () => {
    switch (type) {
      case BREADCRUMB_TYPE.WORKSTREAM:
        return <WorkstreamBreadcrumb to={to} />;
      case BREADCRUMB_TYPE.PLOT:
        return <PlotBreadcrumb />;
      case BREADCRUMB_TYPE.PLOT_GROUP:
        return <PlotGroupBreadcrumb />;
      default:
        return <BreadcrumbSegmentBase label={label} to={to} icon={icon} />;
    }
  };

  return (
    <ErrorBoundary fallback={null}>
      <SuspenseBoundary fallback={null}>
        <BaseComponent />
      </SuspenseBoundary>
    </ErrorBoundary>
  );
};

const BreadcrumbSegmentBase = ({ label, to, icon }: Pick<BreadcrumbItem, 'label' | 'to' | 'icon'>) => {
  if (to) {
    return (
      <ButtonLink variant='text' className='p-2 hover:underline' size='small' to={to}>
        {icon && <Icon icon={icon} />}
        {label}
      </ButtonLink>
    );
  }

  return (
    <span className='typography-button2 text-text-secondary p-2 flex items-center'>
      {icon && <Icon icon={icon} />}
      {label}
    </span>
  );
};

const PlotBreadcrumb = () => {
  const plot = usePlot().data;

  return <BreadcrumbSegmentBase label={plot.name} />;
};

const WorkstreamBreadcrumb = ({ to }: Pick<BreadcrumbItem, 'to'>) => {
  const workstream = useOrganizationWorkstream().data;

  return <BreadcrumbSegmentBase label={workstream.title} to={to} />;
};

const PlotGroupBreadcrumb = () => {
  const plotGroupId = usePlotGroupId();
  const orgId = useOrganizationId();

  const plotGroups = useOrganizationPlotGroups(orgId ?? '').data;

  const plotGroup = plotGroups.find((group) => group.id === plotGroupId);

  if (!plotGroup) {
    return null;
  }

  return <BreadcrumbSegmentBase label={plotGroup.name} />;
};
